import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../Modal.tsx';
import { downloadDefine } from '../../../api/api';
import styles from './DefineHistory.module.css';

interface DefineHistoryItem {
  id: number;
  createdBy: string;
  createdDt: string;
  defineVersion: string;
  studyId: number;
  versionNumber: number;
  revisionNumber: number;
  processingStatus: string;
  defineVersionStatus: string;
}

interface DefineHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  historyData: DefineHistoryItem[];
  studyId: number;
  studyName: string;
}

type FormatType = 'XLSX' | 'XML' | 'HTML';

const DefineHistoryModal: React.FC<DefineHistoryModalProps> = ({
  isOpen,
  onClose,
  historyData,
  studyId,
  studyName,
}) => {
  const navigate = useNavigate();
  const [downloadingItems, setDownloadingItems] = useState<Set<string>>(new Set());
  const [selectedFormat, setSelectedFormat] = useState<FormatType>('XLSX');

  const handleDownload = async (
    versionNumber: number,
    revisionNumber: number,
    format: FormatType
  ) => {
    const downloadKey = `${versionNumber}-${format}`;
    
    try {
      setDownloadingItems(prev => new Set(prev).add(downloadKey));
      
      const response = await downloadDefine(studyId, versionNumber, revisionNumber, format);
      
      // Create a blob and download the file
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `define-v${versionNumber}.${format.toLowerCase()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log(`Downloaded define v${versionNumber} as ${format}`);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert(`Failed to download ${format} file. Please try again.`);
    } finally {
      setDownloadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(downloadKey);
        return newSet;
      });
    }
  };

  const handleView = (id: number, studyId: number) => {
    navigate(`/defineui/excel-viewer/${id}?studyId=${studyId}&studyName=${studyName}`);
  };

  const isDownloading = (versionNumber: number, format: FormatType) => {
    return downloadingItems.has(`${versionNumber}-${format}`);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Define History">
      <div className={styles.tableContainer}>
        <table className={styles.historyTable}>
          <thead>
            <tr>
              <th>User ID</th>
              <th>Date/Time</th>
              <th>Define Version</th>
              <th>Format</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {historyData.length === 0 ? (
              <tr>
                <td colSpan={5} className={styles.emptyState}>
                  No define history available
                </td>
              </tr>
            ) : (
              historyData.map((item) => (
                <tr key={item.id}>
                  <td className={styles.userId}>
                    <div className={styles.userInfo}>
                      <span>{item.createdBy}</span>
                    </div>
                  </td>
                  <td className={styles.dateTime}>{item.createdDt}</td>
                  <td className={styles.version}>
                    <span className={styles.versionBadge}>
                      {item.defineVersion}
                    </span>
                  </td>
                  <td className={styles.formatCell}>
                    <div className={styles.formatButtons}>
                      {(['XLSX', 'XML', 'HTML'] as FormatType[]).map((format) => (
                        <button
                          key={format}
                          className={`${styles.formatButton} ${
                            selectedFormat === format ? styles.selected : ''
                          } ${
                            isDownloading(item.versionNumber, format) ? styles.loading : ''
                          }`}
                          onClick={() => setSelectedFormat(format)}
                          disabled={isDownloading(item.versionNumber, format)}
                          title={`Select ${format} format`}
                        >
                          {format}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className={styles.actionsCell}>
                    <div className={styles.actionButtons}>
                      <button
                        className={styles.downloadButton}
                        onClick={() => handleDownload(item.versionNumber, item.revisionNumber, selectedFormat)}
                        disabled={isDownloading(item.versionNumber, selectedFormat)}
                        title={`Download ${selectedFormat}`}
                      >
                        {isDownloading(item.versionNumber, selectedFormat) ? (
                          <span className={styles.spinner}></span>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                        Download
                      </button>
                      <button
                        className={styles.viewButton}
                        onClick={() => handleView(item.id, item.studyId)}
                        disabled={item.defineVersionStatus !== 'ACTIVE'}
                        title="View Excel Data"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <circle
                            cx="12"
                            cy="12"
                            r="3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Modal>
  );
};

export default DefineHistoryModal;